/* global performance */
import { ReadonlyAnalyserNode } from 'app/services/WebAudio';
import { useCss } from 'app/utils/css';
import React, { FunctionComponent, PureComponent } from 'react';
import useMeasure from '../layout/useMeasure';


interface IProps {
  analyser: ReadonlyAnalyserNode;
  color: string;
}

interface IDeps {
  width: number;
  height: number;
  className: string;
}

class AudioVisualisation extends PureComponent<IProps & IDeps> {
  private gfx?: CanvasRenderingContext2D;
  private target: Uint8Array;
  private last: Uint8Array;
  private lastTime = 0;

  public constructor(props: IProps & IDeps) {
    super(props);
    this.target = new Uint8Array(props.analyser.frequencyBinCount);
    this.last = new Uint8Array(props.analyser.frequencyBinCount);
  }

  public componentDidMount() {
    this.startAudio();
  }

  public render() {
    const { width, height, className } = this.props;
    // TODO add pixelRatio handling
    return (
      <canvas
        key={width * height /* recreate canvas on size change */}
        width={width}
        height={height}
        className={className}
        ref={this.setCanvasRef}
      />
    );
  }

  private draw = () => {
    const { gfx, props: { width, height, color, analyser } } = this;

    if (gfx) {
      const now = performance.now();
      let progress = (now - this.lastTime) / 50;

      if (progress > 1) {
        progress = 0;
        this.lastTime = now;

        // Swap buffers and load new data
        const tmp = this.target;
        this.target = this.last;
        this.last = tmp;
        analyser.getByteFrequencyData(this.target);
      }

      const { last, target } = this;

      const columns = analyser.frequencyBinCount / 2;
      const columnWidth = width / (columns * 2 - 1);

      gfx.clearRect(0, 0, width, height);

      gfx.fillStyle = color;

      for (let i = 0; i < columns; ++i) {
        const value = Math.max(2, (last[i] * (1 - progress) + target[i] * progress) * height / 255);
        gfx.fillRect(columnWidth * (columns - i - 1) + 1, (height - value) / 2, columnWidth + 2, value);
        gfx.fillRect(columnWidth * (columns + i - 1) + 1, (height - value) / 2, columnWidth + 2, value);
      }

      requestAnimationFrame(this.draw);
    }
  };

  private setCanvasRef = (ref: HTMLCanvasElement) => {
    if (this.gfx) {
      this.gfx = undefined;
    }

    if (ref) {
      this.gfx = ref.getContext('2d') || undefined;
      if (!this.gfx) return;

      this.gfx.lineWidth = 2;

      requestAnimationFrame(this.draw);
    }
  };

  private startAudio() {
    const { width } = this.props;
    if (!width) return;

    // Zero out the buffers. We can assume that the buffer size is not changing because
    // the fftSize should not change.
    this.target.fill(0);
    this.last.fill(0);

    requestAnimationFrame(this.draw);
  }
}

const AudioVisualisationInjectDependencies: FunctionComponent<IProps> = (props) => {
  const css = useCss();

  const [{ width, height }, measureRef] = useMeasure();

  return (
    <div
      className={css('sizer', {
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      })}
      ref={measureRef}
    >
      {width > 0 && height > 0 &&
        <AudioVisualisation
          width={width}
          height={height}
          className={css('canvas', {
            display: 'block',
            position: 'absolute',
            top: '0',
            left: '0',
          })}
          {...props}
        />}
    </div>
  );
};

export default AudioVisualisationInjectDependencies;
